<template>
  <div class="p-4">
    <v-card flat rounded="lg" class="order-log-card">
      <v-card-title class="d-flex align-center justify-space-between px-4 py-3">
        <span class="text-h6 font-weight-medium">Order Logs</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="px-4 py-4">
        <div class="d-flex flex-wrap align-end ga-3">
          <DateRangeFilter v-model="dateRange" label="Date range" style="min-width: 320px" />
          <v-select
            v-model="typeFilter"
            :items="typeOptions"
            item-title="label"
            item-value="value"
            label="Type"
            multiple
            clearable
            chips
            variant="outlined"
            density="comfortable"
            hide-details
            style="min-width: 260px"
          />
          <v-text-field
            v-model="codeFilter" label="Stock code (optional)" variant="outlined" density="comfortable"
            hide-details style="max-width: 200px" clearable
          />
          <v-btn color="primary" variant="tonal" rounded="lg" :loading="loading" @click="loadLogs">
            Load
          </v-btn>
        </div>

        <v-alert v-if="loaded && !rows.length" type="info" variant="tonal" density="comfortable" class="mt-4" rounded="lg">
          No order log entries in this range.
        </v-alert>

        <v-data-table
          v-if="rows.length"
          :headers="headers"
          :items="rows"
          :items-per-page-options="[10, 15, 20, 30, 50, 100]"
          :items-per-page="20"
          density="comfortable"
          class="custom-data-table mt-4"
          item-value="order_log_id"
        >
          <template v-slot:[`item.event_time`]="{ item }">
            {{ $helper.formatDate(item.event_time, true) }}
          </template>
          <template v-slot:[`item.event_type`]="{ item }">
            <v-chip size="small" variant="tonal" :color="orderLogTypeColor(item.event_type)">
              {{ orderLogTypeLabel(item.event_type) }}
            </v-chip>
          </template>
          <template v-slot:[`item.code`]="{ item }">
            {{ item.code || '—' }}
          </template>
          <template v-slot:[`item.message`]="{ item }">
            <span class="text-pre-wrap">{{ item.message }}</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import DateRangeFilter from '@/components/Report/filter/DateRangeFilter.vue';
import { ORDER_LOG_TYPES, orderLogTypeLabel, orderLogTypeColor } from '@/utils/orderLogTypes';

function todayDateKey() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Default range: last 7 days, so the page shows something useful on first
// load instead of an empty "pick a range" state.
function daysAgoDateKey(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default {
  name: 'OrderLog-page',
  components: { DateRangeFilter },
  data() {
    return {
      dateRange: { from: daysAgoDateKey(7), to: todayDateKey() },
      typeFilter: [],
      codeFilter: '',
      rows: [],
      loading: false,
      loaded: false,
      typeOptions: Object.entries(ORDER_LOG_TYPES).map(([value, { label }]) => ({ value, label })),
      headers: [
        { title: 'Time', key: 'event_time' },
        { title: 'Type', key: 'event_type' },
        { title: 'Stock', key: 'code' },
        { title: 'Title', key: 'title' },
        { title: 'Message', key: 'message' },
      ],
    };
  },
  mounted() {
    this.loadLogs();
  },
  methods: {
    orderLogTypeLabel,
    orderLogTypeColor,

    toISODate(value) {
      if (!value) return '';
      if (typeof value === 'string') return value.slice(0, 10);
      const d = new Date(value);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },

    loadLogs() {
      const from = this.toISODate(this.dateRange?.from);
      if (!from) return;
      const to = this.toISODate(this.dateRange?.to) || from;
      this.loading = true;

      const params = new URLSearchParams({ from, to });
      if (this.typeFilter?.length) params.set('type', this.typeFilter.join(','));
      if (this.codeFilter) params.set('code', this.codeFilter.trim().toUpperCase());

      return fetchWrapper.get(`api/order-log/list?${params.toString()}`).then((res) => {
        this.rows = res?.data?.rows || [];
        this.loaded = true;
      }).catch((err) => {
        console.error('Failed to load order logs:', err);
        this.rows = [];
        this.loaded = true;
      }).finally(() => {
        this.loading = false;
      });
    },
  },
};
</script>

<style scoped>
.text-pre-wrap {
  white-space: pre-wrap;
}
</style>
